import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { eligibilityRollupStatus, readEligibility } from './eligibility/eligibilityData';
import { buildRowItems, ELIGIBILITY_KINDS, ELIGIBILITY_LABEL, ELIGIBILITY_STATUS } from './CategoryCard';
import { isQuietlyReady } from './referralProgress';
import StatusIcon, { rollupIndicator, StatusIndicator, statusIndicator } from './StatusIcon';

interface RailRow {
  key: string;
  label: string;
  indicator: StatusIndicator;
}

interface RailStep extends RailRow {
  // Shown nested only when a category has more than one action (design §12 roll-up).
  children: RailRow[];
}

// A single synthesized stand-in carrying the folded eligibility status, so the category's
// rollup icon agrees with the collapsed child (e.g. no-active-coverage → attention, not the
// green "all complete" the raw actions would give). Mirrors CategoryCard.headerRollupStatus;
// rollupIndicator only reads these members.
function eligibilityStandIn(status: ActionStatus): WorkflowAction {
  return {
    status,
    stale: false,
    isFlagged: status === ActionStatus.FLAGGED,
    isActionRequired: status === ActionStatus.ACTION_REQUIRED,
    isCompleted: status === ActionStatus.COMPLETED,
  } as WorkflowAction;
}

// One row per category, with action children only where a category has more than
// one action. A category that is entirely per-building copies of a step (fanned out,
// OLIO-2611) gets no children — the buildings live in the category's card, and a rail
// row per building would just repeat the same step label N times. The Payer Search +
// Insurance Verification pair collapses into one "Insurance Eligibility Check" child, and
// its combined state folds into the category icon too (OLIO-2301 — mirrors CategoryCard).
function buildSteps(workflow: Workflow): RailStep[] {
  return workflow.actionsByCategory().map(({ category, actions }) => {
    const eligibilityStatus = actions.some((action) => ELIGIBILITY_KINDS.has(action.kind))
      ? eligibilityRollupStatus(readEligibility(workflow).state)
      : undefined;
    // Fold the eligibility pair into one synthesized status for the category rollup icon.
    const rollupActions =
      eligibilityStatus === undefined
        ? actions
        : [...actions.filter((action) => !ELIGIBILITY_KINDS.has(action.kind)), eligibilityStandIn(eligibilityStatus)];

    return {
      key: category.key,
      label: category.name,
      indicator: actions.length === 1 && isQuietlyReady(actions[0]) ? 'review' : rollupIndicator(rollupActions),
      children:
        actions.length > 1 && !actions.every((action) => action.groupId !== null)
          ? buildRowItems(actions).map((item) =>
              item.kind === 'eligibility'
                ? {
                    key: 'eligibility',
                    label: ELIGIBILITY_LABEL,
                    indicator: ELIGIBILITY_STATUS[readEligibility(workflow).state].indicator,
                  }
                : { key: item.action.id, label: item.action.displayLabel, indicator: statusIndicator(item.action) }
            )
          : [],
    };
  });
}

const isMuted = (indicator: StatusIndicator) => indicator === 'pending' || indicator === 'muted';

export default function ProgressRail({ workflow }: { workflow: Workflow }) {
  const steps = buildSteps(workflow);

  return (
    <Rail data-testid='progress-rail'>
      <BodySmallBold>Referral Progress</BodySmallBold>
      {workflow.createdAt && (
        <LastUpdate>
          <LastUpdateLabel>Last Update:</LastUpdateLabel> Referral created on {simpleDateAtTime(workflow.createdAt)}
        </LastUpdate>
      )}
      <Steps>
        {steps.map((step) => (
          <div key={step.key}>
            <StepRow data-testid={`rail-step-${step.key}`}>
              <StatusIcon indicator={step.indicator} size={18} />
              <StepLabel $muted={isMuted(step.indicator)}>{step.label}</StepLabel>
            </StepRow>
            {step.children.map((child) => (
              <ChildRow key={child.key} data-testid={`rail-child-${child.key}`}>
                <StatusIcon indicator={child.indicator} size={14} />
                <ChildLabel $muted={isMuted(child.indicator)}>{child.label}</ChildLabel>
              </ChildRow>
            ))}
          </div>
        ))}
      </Steps>
    </Rail>
  );
}

const Rail = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  padding: 20px;
`;

const LastUpdate = styled(BodySmall)`
  display: block;
  margin-top: 6px;
  color: ${colors.black50};
`;

const LastUpdateLabel = styled.span`
  font-weight: 700;
  color: ${colors.black75};
`;

const Steps = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-left: 28px;
`;

const StepLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black)};
  font-weight: 700;
`;

const ChildLabel = styled(BodySmall)<{ $muted: boolean }>`
  color: ${({ $muted }) => ($muted ? colors.black50 : colors.black75)};
`;
